import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import HelpCircleIcon from "@redotech/redo-icons/help-circle.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import type { DynamicToolUIPart } from "ai";
import { memo, useEffect, useRef, useState } from "react";
import { AskRedoAiStarSpinner } from "./ask-redo-ai-star-spinner";
import { AskRedoDisclosureButton } from "./ask-redo-disclosure-button";
import * as styles from "./ask-redo-tool-group.module.css";
import { formatToolLabel, isToolHidden } from "./tool-summaries";

/**
 * Renders a contiguous run of tool calls from one assistant turn.
 *
 * Layout depends on how many *visible* tools the group has after
 * filtering out Tier-0 (hidden) ones:
 *
 *   - **0 visible** → render nothing. The group was entirely
 *     agent-internal noise (skill load + catalog pre-flights, etc.).
 *   - **1 visible** → a single flat row, no disclosure: the friendly
 *     label IS the whole story.
 *   - **2+ visible** → an aggregate disclosure header ("Used 3
 *     tools ⌄") that expands to the same flat list. Keeps the
 *     conversation compact for multi-tool turns while still letting
 *     the merchant see what happened.
 *
 * Each row reads:
 *   ✓ <friendly merchant-facing label>
 *
 * Friendly labels come from `tool-summaries.ts`. There is
 * intentionally no JSON viewer in this surface — the raw shapes
 * were useful to engineers and noise to merchants. Power users can
 * still see them via React DevTools or merchant-server logs.
 */
export interface AskRedoToolGroupProps {
  tools: readonly DynamicToolUIPart[];
}

export const AskRedoToolGroup = memo(function AskRedoToolGroup({
  tools,
}: AskRedoToolGroupProps) {
  const visibleTools = tools.filter((t) => !isToolHidden(t.toolName));
  if (visibleTools.length === 0) {
    return null;
  }
  if (visibleTools.length === 1) {
    return (
      <div className={styles.group}>
        {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
        <ToolRow tool={visibleTools[0]!} />
      </div>
    );
  }
  return <MultiToolGroup tools={visibleTools} />;
});

const MultiToolGroup = memo(function MultiToolGroup({
  tools,
}: {
  tools: readonly DynamicToolUIPart[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const overall = useStableRunningState(computeOverallState(tools));
  const total = tools.length;
  const done = tools.filter(
    (t) => classifyToolState(t.state) === "done",
  ).length;

  // Aggregate header text — "Using N tools…" while in-flight,
  // "Used N tools" once everything settles, error callout if any
  // tool errored.
  let summary: string;
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (overall) {
    case "running": {
      summary =
        done > 0
          ? `Using ${total} tools… ${done} of ${total}`
          : `Using ${total} tools…`;

      break;
    }
    case "error": {
      summary = `Used ${total} tools — one failed`;

      break;
    }
    case "awaiting-approval": {
      summary = "Waiting for your confirmation";

      break;
    }
    default: {
      summary = `Used ${total} tools`;
    }
  }

  return (
    <div className={styles.group}>
      <AskRedoDisclosureButton
        isOpen={isOpen}
        onToggle={() => setIsOpen((v) => !v)}
      >
        {renderIcon(overall)}
        <Text
          className={styles.rowLabel}
          fontSize="xs"
          minW="none"
          textColor="secondary"
        >
          {summary}
        </Text>
      </AskRedoDisclosureButton>
      {isOpen && (
        <div className={styles.expandedRows}>
          {tools.map((tool) => (
            <ToolRow key={tool.toolCallId} tool={tool} />
          ))}
        </div>
      )}
    </div>
  );
});

const ToolRow = memo(function ToolRow({ tool }: { tool: DynamicToolUIPart }) {
  const state = useStableRunningState(classifyToolState(tool.state));

  // `formatToolLabel` returns null only for tools registered as
  // hidden. We already filtered those out in `AskRedoToolGroup`, but
  // re-check defensively so a future caller that bypasses the parent
  // filter doesn't accidentally render an "undefined" string.
  const label = formatToolLabel(
    tool.toolName,
    tool.input,
    tool.state === "output-available" ? tool.output : undefined,
  );
  if (label === null) {
    return null;
  }

  return (
    <Flex
      align="center"
      alignSelf="flex-start"
      className={styles.row}
      gap="sm"
      maxW="full"
      minW="none"
      px="sm"
      py="xxs"
    >
      {renderIcon(state)}
      <Text
        className={styles.rowLabel}
        fontSize="xs"
        minW="none"
        textColor="secondary"
      >
        {state === "running" ? `${label}…` : label}
      </Text>
    </Flex>
  );
});

type ToolState = "running" | "done" | "error" | "awaiting-approval";

/**
 * Minimum time the "running" state stays visible once it appears.
 * Fast tool calls (~50ms) would otherwise flash the spinner and
 * snap to the done check before the merchant can register it. With
 * this floor, "Using N tools…" is visible for at least this long
 * so the running → done transition feels like a deliberate beat
 * rather than a flicker.
 *
 * 400ms and 800ms both still flashed too quickly in testing. 1500ms
 * is the floor that gives the running label a clear beat — the
 * merchant has time to actually read "Using 2 tools…" before the
 * swap. The streaming response that follows masks any perceived
 * delay since the assistant's text starts arriving soon after.
 */
const MIN_RUNNING_DISPLAY_MS = 1500;

/**
 * Holds the displayed state at "running" for at least
 * `MIN_RUNNING_DISPLAY_MS` after it first transitions in. If a tool
 * completes faster than that, the swap to "done" is deferred to the
 * remainder of the minimum. If the tool never enters running (mounts
 * directly to done), no delay is applied — we only smooth genuine
 * flicker, not artificially slow down already-instant turns.
 */
function useStableRunningState(state: ToolState): ToolState {
  const [displayed, setDisplayed] = useState(state);
  const runningSince = useRef<number | null>(
    state === "running" ? Date.now() : null,
  );

  useEffect(() => {
    if (state === "running") {
      if (runningSince.current === null) {
        runningSince.current = Date.now();
      }
      setDisplayed("running");
      return;
    }
    if (runningSince.current === null) {
      // Skipped past running entirely — no smoothing needed.
      setDisplayed(state);
      return;
    }
    const elapsed = Date.now() - runningSince.current;
    const remaining = MIN_RUNNING_DISPLAY_MS - elapsed;
    if (remaining <= 0) {
      setDisplayed(state);
      runningSince.current = null;
      return;
    }
    const timer = setTimeout(() => {
      setDisplayed(state);
      runningSince.current = null;
    }, remaining);
    return () => clearTimeout(timer);
  }, [state]);

  return displayed;
}

function computeOverallState(tools: readonly DynamicToolUIPart[]): ToolState {
  if (tools.some((t) => classifyToolState(t.state) === "error")) {
    return "error";
  }
  if (tools.some((t) => classifyToolState(t.state) === "awaiting-approval")) {
    return "awaiting-approval";
  }
  if (tools.some((t) => classifyToolState(t.state) === "running")) {
    return "running";
  }
  return "done";
}

function classifyToolState(state: DynamicToolUIPart["state"]): ToolState {
  switch (state) {
    case "output-available":
      return "done";
    case "output-error":
    case "output-denied":
      return "error";
    case "approval-requested":
    case "approval-responded":
      // The parent renderer normally pulls approval-* tools out of
      // the group and shows a Confirmation card; if a tool ever
      // lands here in either state we want a non-spinning cue.
      return "awaiting-approval";
    case "input-streaming":
    case "input-available":
      return "running";
  }
}

function renderIcon(state: ToolState): React.ReactNode {
  switch (state) {
    case "done":
      return (
        <RedoIcon
          className={styles.statusIcon}
          color="success"
          Icon={CheckCircleIcon}
          size={14}
        />
      );
    case "error":
      return (
        <RedoIcon
          className={styles.statusIcon}
          color="error"
          Icon={XCircleIcon}
          size={14}
        />
      );
    case "awaiting-approval":
      return (
        <RedoIcon
          className={styles.statusIcon}
          color="warning"
          Icon={HelpCircleIcon}
          size={14}
        />
      );
    case "running":
      return <AskRedoAiStarSpinner />;
  }
}
